import React, { useEffect, useState } from "react";
import api from "@/api/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, KpiCard, Panel } from "@/components/shared/PageUi";
import CrudList from "@/components/shared/CrudList";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import { Car, Wrench, AlertTriangle, ShieldCheck, Users } from "lucide-react";

const VEHICLE_STATUS = [
  { value: "active", label: "Käytössä" },
  { value: "service", label: "Huollossa" },
  { value: "out_of_use", label: "Poissa käytöstä" },
  { value: "sold", label: "Myyty" },
];
const FUEL_TYPES = [
  { value: "petrol", label: "Bensiini" },
  { value: "diesel", label: "Diesel" },
  { value: "electric", label: "Sähkö" },
  { value: "hybrid", label: "Hybridi" },
];
const CATEGORIES = [
  { value: "car", label: "Auto" },
  { value: "van", label: "Pakettiauto" },
  { value: "truck", label: "Kuorma-auto" },
  { value: "machine", label: "Työkone" },
];
const ASSIGNED_MODULES = [
  { value: "taxi", label: "Taksi" },
  { value: "logistics", label: "Logistiikka" },
  { value: "car_rental", label: "Autovuokraus" },
  { value: "auto_repair", label: "Autokorjaamo" },
  { value: "other", label: "Muu" },
];
const MAINT_STATUS = [
  { value: "scheduled", label: "Varattu" },
  { value: "in_progress", label: "Käynnissä" },
  { value: "completed", label: "Valmis" },
  { value: "cancelled", label: "Peruutettu" },
];
const MAINT_TYPES = [
  { value: "service", label: "Huolto" },
  { value: "repair", label: "Korjaus" },
  { value: "inspection", label: "Katsastus" },
  { value: "tires", label: "Renkaat" },
  { value: "insurance", label: "Vakuutus" },
];

export default function FleetDashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([api.get("/fleet/stats"), api.get("/fleet/alerts")])
      .then(([s, a]) => { setStats(s.data); setAlerts(a.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const vehicles = {
    endpoint: "/fleet/vehicles",
    testidPrefix: "fleet-vehicles",
    emptyMessage: "Ei ajoneuvoja",
    defaults: { plate_number: "", status: "active", mileage_km: 0, year: new Date().getFullYear(), purchase_price: 0 },
    fields: [
      { key: "plate_number", label: "Rekisterinumero", type: "text", required: true },
      { key: "brand", label: "Merkki", type: "text" },
      { key: "model", label: "Malli", type: "text" },
      { key: "year", label: "Vuosi", type: "number", step: "1" },
      { key: "vin", label: "VIN", type: "text" },
      { key: "fuel_type", label: "Polttoaine", type: "select", options: FUEL_TYPES },
      { key: "category", label: "Kategoria", type: "select", options: CATEGORIES },
      { key: "assigned_module", label: "Käytössä moduulissa", type: "select", options: ASSIGNED_MODULES },
      { key: "current_driver", label: "Nykyinen kuljettaja", type: "text" },
      { key: "status", label: "Tila", type: "select", options: VEHICLE_STATUS },
      { key: "mileage_km", label: "Mittarilukema km", type: "number", step: "1" },
      { key: "purchase_date", label: "Hankintapäivä", type: "date" },
      { key: "purchase_price", label: "Hankintahinta €", type: "number" },
      { key: "next_service_date", label: "Seur. huolto pvm", type: "date" },
      { key: "next_service_km", label: "Seur. huolto km", type: "number", step: "1" },
      { key: "last_service_date", label: "Edell. huolto pvm", type: "date" },
      { key: "insurance_expiry", label: "Vakuutus päättyy", type: "date" },
      { key: "inspection_expiry", label: "Katsastus päättyy", type: "date" },
      { key: "image_url", label: "Kuvan URL", type: "text", span: 2 },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "plate_number", label: "Rek." },
      { key: "brand", label: "Merkki" },
      { key: "model", label: "Malli" },
      { key: "year", label: "Vuosi", align: "right" },
      { key: "mileage_km", label: "Km", format: "num", align: "right" },
      { key: "assigned_module", label: "Moduuli", format: "badge" },
      { key: "status", label: "Tila", format: "badge" },
    ],
    getRowLabel: (v) => `${v.plate_number} ${v.brand || ""} ${v.model || ""}`,
  };

  const maintenance = {
    endpoint: "/fleet/maintenance",
    testidPrefix: "maint",
    emptyMessage: "Ei huoltohistoriaa",
    defaults: { type: "service", description: "", service_date: new Date().toISOString().slice(0, 10), cost: 0, status: "completed" },
    fields: [
      { key: "vehicle_plate", label: "Ajoneuvo (rek.nro)", type: "text" },
      { key: "type", label: "Tyyppi", type: "select", options: MAINT_TYPES },
      { key: "service_date", label: "Päivämäärä", type: "date", required: true },
      { key: "mileage_at_service", label: "Mittarilukema", type: "number", step: "1" },
      { key: "cost", label: "Kustannus €", type: "number" },
      { key: "workshop", label: "Korjaamo", type: "text" },
      { key: "status", label: "Tila", type: "select", options: MAINT_STATUS },
      { key: "next_service_date", label: "Seur. huolto pvm", type: "date" },
      { key: "next_service_km", label: "Seur. huolto km", type: "number", step: "1" },
      { key: "description", label: "Kuvaus", type: "textarea", span: 2 },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "service_date", label: "Pvm" },
      { key: "vehicle_plate", label: "Ajoneuvo" },
      { key: "type", label: "Tyyppi", format: "badge" },
      { key: "description", label: "Kuvaus" },
      { key: "workshop", label: "Korjaamo" },
      { key: "cost", label: "€", format: "eur", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  return (
    <div className="space-y-6" data-testid="fleet-dashboard">
      <PageHeader kicker="Sovellus" title="Kalusto" description="Ajoneuvot, huollot ja vakuutus-/katsastuspäättymiset." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<Car className="w-5 h-5" />} label="Käytössä" value={stats?.vehicles_active ?? "—"} accent="text-emerald-300" loading={loading} testid="fleet-kpi-active" />
        <KpiCard icon={<Wrench className="w-5 h-5" />} label="Huollossa" value={stats?.vehicles_in_service ?? 0} accent="text-amber-300" loading={loading} testid="fleet-kpi-service" />
        <KpiCard icon={<ShieldCheck className="w-5 h-5" />} label="Vakuutus < 30 pv" value={stats?.expiring_insurance_30d ?? 0} accent={(stats?.expiring_insurance_30d || 0) > 0 ? "text-red-300" : "text-emerald-300"} loading={loading} testid="fleet-kpi-ins" />
        <KpiCard icon={<AlertTriangle className="w-5 h-5" />} label="Katsastus < 30 pv" value={stats?.expiring_inspection_30d ?? 0} accent={(stats?.expiring_inspection_30d || 0) > 0 ? "text-red-300" : "text-emerald-300"} loading={loading} testid="fleet-kpi-insp" />
      </div>

      {alerts.length > 0 && (
        <Panel title="Päättymässä olevat" className="border-amber-500/30">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {alerts.map((a) => (
              <div key={`${a.id}-${a.type}`} className="rounded-lg border border-amber-500/20 bg-black/20 px-3 py-2">
                <p className="text-xs uppercase tracking-wider text-amber-300">{a.type === "insurance" ? "Vakuutus" : "Katsastus"}</p>
                <p className="font-medium">{a.plate_number} · {a.brand} {a.model}</p>
                <p className="text-sm text-red-300">Päättyy: {a.expires}</p>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="Hallinta">
        <Tabs defaultValue="vehicles" className="w-full">
          <TabsList className="bg-amber-500/10 mb-4">
            <TabsTrigger value="vehicles" data-testid="fleet-tab-vehicles">Ajoneuvot</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="fleet-tab-maint">Huollot</TabsTrigger>
            <TabsTrigger value="staff" data-testid="fleet-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
          </TabsList>
          <TabsContent value="vehicles"><CrudList config={vehicles} /></TabsContent>
          <TabsContent value="maintenance"><CrudList config={maintenance} /></TabsContent>
          <TabsContent value="staff"><ModuleStaffTab moduleKey="fleet" moduleLabel="Kalusto" /></TabsContent>
        </Tabs>
      </Panel>
    </div>
  );
}
